import React from 'react'
import DataRepo from '@api/datasource/data'
import QueryKeys from '@constants/queryKeys'

import { CreateEventStatusSchema, CreateEventStatusType, StatusEventType } from '@customTypes/event'

import { ModalProps } from '@customTypes/page'
import { useIsMobile } from '@hooks/mobile'
import { Button, LoadingOverlay, Modal, Text } from '@mantine/core'
import { useMutation, useQuery } from '@tanstack/react-query'
import { isLoadingMutation, isLoadingQuery } from '@utils/network'
import { useForm, zodResolver } from '@mantine/form'
import queryClient from '@api/datasource/query'
import { notifications } from '@mantine/notifications'
import Input from '@components/shared/input'
import ConfirmationModal from '@components/shared/confirmationModal'
import Cont from '@components/shared/cont'

const INITIAL: CreateEventStatusType = { name: '', color: '#FFFFFF', order: 1 }

const EventStatusForm = (props: ModalProps<StatusEventType>) => {
  const { open, modal, data, size = 'lg', onClose: outerOnClose } = props

  const isMobile = useIsMobile()

  const [confirmExit, setConfirmExit] = React.useState(false)

  const formEventStatus = useForm<CreateEventStatusType>({
    initialValues: INITIAL,
    validate: zodResolver(CreateEventStatusSchema),
  })

  const EventStatusQuery = useQuery({
    queryKey: [QueryKeys.GET_EVENT_STATUS_KEY],
    queryFn: () => DataRepo.clientEntityService.getEventStatusOptions(),
    enabled: Boolean(open),
  })

  const createEventStatusMutation = useMutation<StatusEventType, Error, CreateEventStatusType>({
    mutationFn: (values) => DataRepo.clientEntityService.createEventStatus(values),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_EVENT_STATUS_KEY],
        refetchType: 'all',
      })

      formEventStatus.resetDirty()
      formEventStatus.reset()
      outerOnClose?.()

      notifications.show({
        message: 'Tipo de estado creado correctamente',
        title: 'Éxito',
        color: 'green',
      })
    },
    onError: (error) => {
      notifications.show({
        message: error.message || 'Error al crear el tipo de estado',
        title: 'Error',
        color: 'red',
      })
    },
  })

  const updateEventStatusMutation = useMutation<void, Error, StatusEventType>({
    mutationFn: (values) => DataRepo.clientEntityService.updateEventStatus(values),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_EVENT_STATUS_KEY],
        refetchType: 'all',
      })

      formEventStatus.resetDirty()
      formEventStatus.reset()
      outerOnClose?.()

      notifications.show({
        message: 'Tipo de estado actualizado correctamente',
        title: 'Éxito',
        color: 'green',
      })
    },
    onError: (error) => {
      notifications.show({
        message: error.message || 'Error al actualizar el tipo de estado',
        title: 'Error',
        color: 'red',
      })
    },
  })

  React.useEffect(() => {
    if (open && data) {
      formEventStatus.setValues(data)
    } else if (!open) {
      formEventStatus.setValues(INITIAL)
    }
    formEventStatus.resetDirty()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, data])

  const isLoading = isLoadingMutation(createEventStatusMutation, updateEventStatusMutation)

  const isLoadingEventStatus = isLoadingQuery(EventStatusQuery)

  const content = (
    <form
      onSubmit={formEventStatus.onSubmit((values) => {
        const existingEventStatus = EventStatusQuery.data?.some(
          (status) => status.name === values.name,
        )
        const isSameEventStatus = data && data.name === values.name

        if (existingEventStatus && !isSameEventStatus) {
          notifications.show({
            message: 'Ya existe un estado con ese nombre',
            title: 'Error',
            color: 'red',
          })
          return
        }

        if (data) {
          updateEventStatusMutation.mutate(values as StatusEventType)
        } else {
          createEventStatusMutation.mutate(values)
        }
      })}
    >
      <div className="cd-flex cd-flex-col cd-gap-y-[1rem]">
        <LoadingOverlay
          loaderProps={{ type: 'dots' }}
          overlayProps={{ radius: 'sm', blur: 2 }}
          visible={isLoadingEventStatus}
          zIndex={1000}
        />
        <Input
          capitalize
          label="Nombre"
          placeholder="Ingrese el nombre"
          typeInput="text"
          {...formEventStatus.getInputProps('name')}
          onBlur={() => formEventStatus.validateField('name')}
        />

        <Input
          label="Color"
          typeInput="color"
          withEyeDropper={false}
          {...formEventStatus.getInputProps('color')}
          onBlur={() => formEventStatus.validateField('color')}
        />

        <Button
          className="cd-mt-[1rem]"
          disabled={!formEventStatus.isValid()}
          loaderProps={{ type: 'dots' }}
          loading={isLoading}
          type="submit"
        >
          {data ? 'Actualizar Estado de Evento' : 'Crear Estado de Evento'}
        </Button>
      </div>
    </form>
  )

  if (modal) {
    return (
      <Modal
        centered
        fullScreen={isMobile}
        opened={Boolean(open)}
        size={size}
        title={data ? 'Actualizar Estado de Evento' : 'Crear Estado de Evento'}
        withCloseButton={modal}
        onClose={() => {
          if (formEventStatus.isDirty()) {
            setConfirmExit(true)
          } else {
            onClose()
          }
        }}
      >
        {content}
        <ConfirmationModal
          cancelColor="gray"
          cancelText="Cancelar"
          confirmColor="red"
          confirmText="Descartar"
          open={confirmExit}
          title="Descartar cambios"
          onCancel={() => setConfirmExit(false)}
          onConfirm={onClose}
        >
          <Text className="cd-text-base">
            Has realizado cambios en el formulario, ¿Deseas cerrarlo y descartar los cambios?
          </Text>
        </ConfirmationModal>
      </Modal>
    )
  }

  return <Cont>{content}</Cont>

  function onClose() {
    outerOnClose?.()
    setConfirmExit(false)
    formEventStatus.reset()
  }
}

export default EventStatusForm
